import { Checkbox, FormControlLabel, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { KeyboardEvent, useRef, useMemo } from "react";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { Configuration } from "./components";
import { useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { AppliedFilters } from "../filter/components";

const mapStateToProps = (state: RootState) => ({
  configurationsState: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationsState,
  isLoading: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.isLoading,
  selectedConfigurations: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.selectedConfigurations,
  appliedFilter: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter
});

const ownActions = {
  setSelectedConfigurations: configuratorActions.setSelectedConfigurations
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ConfigurationsListBase = (props: Props) => {
  const { configurationsState, selectedConfigurations, setSelectedConfigurations, isLoading, appliedFilter } = props;

  const theme = useTheme();
  const listRef = useRef<HTMLDivElement>(null);

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const handleListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const summaries = Array.from(listRef.current?.querySelectorAll<HTMLElement>(".MuiAccordionSummary-root") ?? []);
    const idx = summaries.indexOf(document.activeElement as HTMLElement);
    if (idx === -1) return;
    e.preventDefault();
    const next = e.key === "ArrowDown" ? summaries[idx + 1] : summaries[idx - 1];
    next?.focus();
  };

  const isAllSelected = useMemo(() => {
    if (configurationsState) return selectedConfigurations.length === configurationsState.configurations.length;
    return false;
  }, [selectedConfigurations, configurationsState]);

  if (isLoading) {
    return (
      <Stack gap={2}>
        {Array(4)
          .fill(null)
          .map((_, index) => {
            return <Skeleton key={index} height={40} />;
          })}
      </Stack>
    );
  }

  if (!isLoading && configurationsState)
    return (
      <Stack>
        {Object.values(appliedFilter).some(x => x.length > 0) && (
          <Stack pb={1}>
            <AppliedFilters />
          </Stack>
        )}
        {configurationsState.configurations.length === 0 && (
          <Stack gap={2} mt={2}>
            <Typography variant="subtitle2" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.noConfigurations}
            </Typography>
            <Typography variant="subtitle1" color={theme.palette.customColors.darkGrey}>
              {configuratorPageT.getStartedByClickingNewConfiguration}
            </Typography>
          </Stack>
        )}

        {configurationsState.configurations.length > 0 && (
          <Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mx={1}>
              <FormControlLabel
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: theme.typography.subtitle1.fontSize,
                    fontWeight: "normal",
                    fontFamily: "AvenirNextLTPro, sans-serif"
                  }
                }}
                checked={isAllSelected}
                onChange={(_, checked) => {
                  if (checked) setSelectedConfigurations(configurationsState.configurations.map(x => x.configurationId?.toString() || ""));
                  else setSelectedConfigurations([]);
                }}
                control={<Checkbox title={genericTranslationsT.selectAll} size="small" />}
                label={genericTranslationsT.selectAll}
              />

              <Typography variant="subtitle1" color={theme.palette.customColors.darkGrey}>
                {configurationsState.configurations.length} {configurationsState.configurations.length > 1 ? configuratorPageT.configurations : configuratorPageT.configuration}
              </Typography>
            </Stack>
            <Stack ref={listRef} gap={1} onKeyDown={handleListKeyDown}>
              {configurationsState.configurations.map(myConfiguration => {
                return <Configuration key={myConfiguration.configurationId} configuration={myConfiguration} />;
              })}
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  return <></>;
};

export const ConfigurationsList = connect(mapStateToProps, ownActions)(ConfigurationsListBase);
